import { useCallback, useEffect, useRef, useState } from 'react';

import { startChannelCrawl, fetchChannelCrawlDetails } from 'api/line';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';

type CrawlPhase = 'idle' | 'starting' | 'crawling' | 'confirm' | 'completed' | 'error';

type CrawlProgress = {
  phase: CrawlPhase;
  jobId: number | null;
  totalMembers: number;
  crawledMembers: number;
  error?: string;
};

type UseChannelCrawlResult = {
  startCrawl: (url: string, loginId: string, loginPass: string, organizationId?: number) => Promise<void>;
  isRunning: boolean;
  progress: CrawlProgress;
  reset: () => void;
};

const initialProgress: CrawlProgress = {
  phase: 'idle',
  jobId: null,
  totalMembers: 0,
  crawledMembers: 0,
};

export function useChannelCrawl(): UseChannelCrawlResult {
  const { user } = useAuth();
  const strings = lineStrings.management.migration.crawl;
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<CrawlProgress>(initialProgress);
  const abortRef = useRef(false);

  useEffect(() => () => { abortRef.current = true; }, []);

  const startCrawlFn = useCallback(
    async (url: string, loginId: string, loginPass: string, organizationId?: number) => {
      if (!user) {
        setProgress({ ...initialProgress, phase: 'error', error: strings.errors.unauthorized });
        return;
      }

      abortRef.current = false;
      setIsRunning(true);
      setProgress({ ...initialProgress, phase: 'starting' });

      try {
        const { jobId, totalMembers } = await startChannelCrawl({ url, loginId, loginPass }, organizationId);
        setProgress((prev) => ({ ...prev, phase: 'crawling', jobId, totalMembers }));

        let crawlDone = false;
        while (!crawlDone && !abortRef.current) {
          const result = await fetchChannelCrawlDetails(jobId, organizationId);
          setProgress((prev) => ({
            ...prev,
            crawledMembers: result.crawledMembers,
            totalMembers: result.totalMembers,
          }));
          crawlDone = result.done;
          if (!crawlDone) await new Promise((r) => setTimeout(r, 1000));
        }

        if (abortRef.current) return;

        setProgress((prev) => ({ ...prev, phase: 'confirm' }));
      } catch (caught) {
        const error = caught instanceof Error ? caught.message : strings.errors.channelCrawlFailed;
        setProgress((prev) => ({ ...prev, phase: 'error', error }));
      } finally {
        setIsRunning(false);
      }
    },
    [user, strings],
  );

  const reset = useCallback(() => {
    abortRef.current = true;
    setIsRunning(false);
    setProgress(initialProgress);
  }, []);

  return {
    startCrawl: startCrawlFn,
    isRunning,
    progress,
    reset,
  };
}
